import _ from "lodash";
import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { getAIPromptsQuery } from "modules/CommonQuery";
import {
  getCurrentCurriculumProgramType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import useSelector from "Hooks/useSelector";
import { getAIPromptsFilter } from "ClassRoom/components/AddAssignment/AiQuestionAssistant/modules/Utils";
import { getIsWorksheetAiAddCategoryFilter } from "../modules/WorksheetFeatureFlags";

const CONSTANTS = {
  DISPLAY_PROMPT_TYPE: "display",
  WORKSHEET_GENERATE_P1: "worksheet_generate_p1",
  WORKSHEET_GENERATE_P2: "worksheet_generate_p2",
};

const selectPortalType = state => _.get(state, "login.userInfo.portalType");

/**
 * Fetches AI constant prompts filtered by entity context.
 *
 * @param { entityId, extraFilters}  args
 * @returns {{ prompts: Array, isLoading: boolean }}
 */
export const useWorksheetAiConstantPrompts = ({
  entityId,
  entityType,
  assessmentDetails,
}) => {
  const skip = !entityId;
  const portalType = useSelector(selectPortalType);
  const curriculumType = useSelector(getCurrentCurriculumProgramType);
  const curriculumProgramId = useSelector(getSelectedCurriculumId);
  const queryVariables = useMemo(() => {
    const extraFilters = getAIPromptsFilter(assessmentDetails, curriculumType);
    if (getIsWorksheetAiAddCategoryFilter()) {
      extraFilters.categories = [
        { entityType: "CUSTOM", entities: curriculumType },
      ];
    }
    return {
      filters: {
        entityId,
        entityType,
        curriculumProgramId,
        ...(extraFilters || {}),
      },
      portalType,
    };
  }, [
    entityId,
    entityType,
    portalType,
    curriculumType,
    curriculumProgramId,
    assessmentDetails,
  ]);

  const { data, networkStatus } = useQuery(getAIPromptsQuery, {
    skip,
    variables: queryVariables,
    fetchPolicy: "cache-and-network",
  });

  const allPrompts = _.get(data, "ai.prompts", []);

  const actionPrompts = _.map(
    [CONSTANTS.WORKSHEET_GENERATE_P1, CONSTANTS.WORKSHEET_GENERATE_P2],
    action => ({ ..._.find(allPrompts, { action }), createUserMessage: false })
  );

  const presetPrompts = _.filter(allPrompts, {
    promptType: CONSTANTS.DISPLAY_PROMPT_TYPE,
  });

  return {
    data: { allPrompts, actionPrompts, presetPrompts },
    isLoading: _.includes([1, 2], networkStatus),
  };
};
